import { BudgetStatus } from "./budgeting.enums";

/**
 * Centralized, backend-authoritative budget lifecycle state machine.
 *
 * Why a typed transition map and not XState: the repo has no existing XState usage, the graph is small
 * and fixed, and the backend must be the source of truth regardless of any client-side machine. A pure
 * typed map gives the same rigor (single definition of legal transitions + the capability each requires)
 * while staying dependency-free and shareable across `apps/api`, `apps/tenant`, and `apps/admin`.
 *
 * The backend service is authoritative: it validates every transition against {@link findBudgetTransition}
 * AND re-checks data guards (e.g. "has at least one income + one expense line") AND the CASL capability
 * before mutating. The frontend uses the same map purely to decide which actions to OFFER.
 *
 * States are the lowercase {@link BudgetStatus} values; the persisted (Prisma) status is the UPPER_SNAKE
 * form, so the backend lowercases (`row.status.toLowerCase()`) before consulting this map.
 */

/** Capability gate for a transition — maps 1:1 to a CASL budget action on the backend. */
export enum BudgetCapability {
  /** View budgets. */
  View = "view",
  /** Create a budget. */
  Create = "create",
  /** Edit metadata / lines — only legal while DRAFT. */
  Edit = "edit",
  /** Submit for review / move back to draft / resubmit (editor-side). */
  Submit = "submit",
  /** Pick up a submitted budget to review (approver-side). */
  Review = "review",
  /** Approve a reviewed budget. */
  Approve = "approve",
  /** Return a reviewed budget with correction notes. */
  RequestChanges = "request_changes",
  /** Mark an approved budget ready for execution. */
  MarkReady = "mark_ready",
  /** Revise a running budget (returns it to draft, audited + versioned). */
  Revise = "revise",
  /** Close a running budget. */
  Close = "close",
  /** Archive a budget. */
  Archive = "archive",
  /** Restore an archived budget to draft. */
  Restore = "restore",
}

/** Stable identifier for each lifecycle action (used in routes, audit logs, and UI button keys). */
export enum BudgetTransitionAction {
  Submit = "submit",
  PickUpReview = "pick_up_review",
  Approve = "approve",
  RequestChanges = "request_changes",
  ReturnToDraft = "return_to_draft",
  Resubmit = "resubmit",
  MarkReady = "mark_ready",
  AutoStart = "auto_start",
  Revise = "revise",
  Close = "close",
  Archive = "archive",
  Restore = "restore",
}

export type BudgetTransition = {
  action: BudgetTransitionAction;
  to: BudgetStatus;
  /** Required budget capability. `null` means system-only (e.g. cron auto-start) — no user may trigger it. */
  capability: BudgetCapability | null;
  /** True when only the system (cron/job) may perform the transition. */
  system: boolean;
};

const t = (
  action: BudgetTransitionAction,
  to: BudgetStatus,
  capability: BudgetCapability | null,
  system = false,
): BudgetTransition => ({ action, to, capability, system });

/** Archive is allowed from every non-terminal state; defined once and appended where applicable. */
const ARCHIVE = t(BudgetTransitionAction.Archive, BudgetStatus.Archived, BudgetCapability.Archive);

/**
 * The complete legal transition graph. Each key lists the transitions allowed OUT of that state.
 * Mirrors the product brief exactly (incl. the system-only `READY → IN_PROGRESS` auto-start and the
 * audited `IN_PROGRESS → DRAFT` revise).
 */
export const BUDGET_TRANSITIONS: Record<BudgetStatus, BudgetTransition[]> = {
  [BudgetStatus.Draft]: [
    t(BudgetTransitionAction.Submit, BudgetStatus.ReadyForReview, BudgetCapability.Submit),
    ARCHIVE,
  ],
  [BudgetStatus.ReadyForReview]: [
    t(BudgetTransitionAction.PickUpReview, BudgetStatus.InReview, BudgetCapability.Review),
    ARCHIVE,
  ],
  [BudgetStatus.InReview]: [
    t(BudgetTransitionAction.Approve, BudgetStatus.Approved, BudgetCapability.Approve),
    t(BudgetTransitionAction.RequestChanges, BudgetStatus.ChangesRequested, BudgetCapability.RequestChanges),
    ARCHIVE,
  ],
  [BudgetStatus.ChangesRequested]: [
    t(BudgetTransitionAction.ReturnToDraft, BudgetStatus.Draft, BudgetCapability.Edit),
    t(BudgetTransitionAction.Resubmit, BudgetStatus.ReadyForReview, BudgetCapability.Submit),
    ARCHIVE,
  ],
  [BudgetStatus.Approved]: [
    t(BudgetTransitionAction.MarkReady, BudgetStatus.Ready, BudgetCapability.MarkReady),
    ARCHIVE,
  ],
  [BudgetStatus.Ready]: [
    // System-only: cron moves READY -> IN_PROGRESS once the start date arrives (within the window).
    t(BudgetTransitionAction.AutoStart, BudgetStatus.InProgress, null, true),
    // Planning-phase correction: the same "Return to draft" action, available BEFORE the start date.
    // The backend additionally guards on "not yet started" (date guards live in the service, not the
    // map) and records NO version — nothing has executed. Invalidates the prior approval (re-review
    // needed). Gated by the MarkReady capability — the approver tier that manages the approved→ready
    // lifecycle can also pull a ready budget back to draft before it starts.
    t(BudgetTransitionAction.ReturnToDraft, BudgetStatus.Draft, BudgetCapability.MarkReady),
    ARCHIVE,
  ],
  [BudgetStatus.InProgress]: [
    // Revising a running budget returns it to DRAFT with an audit trail + version snapshot.
    t(BudgetTransitionAction.Revise, BudgetStatus.Draft, BudgetCapability.Revise),
    t(BudgetTransitionAction.Close, BudgetStatus.Closed, BudgetCapability.Close),
    ARCHIVE,
  ],
  [BudgetStatus.Closed]: [ARCHIVE],
  [BudgetStatus.Archived]: [
    t(BudgetTransitionAction.Restore, BudgetStatus.Draft, BudgetCapability.Restore),
  ],
};

/** All transitions legal out of `from`. */
export function getBudgetTransitions(from: BudgetStatus): BudgetTransition[] {
  return BUDGET_TRANSITIONS[from] ?? [];
}

/** User-triggerable transitions out of `from` (excludes system-only transitions like auto-start). */
export function getUserBudgetTransitions(from: BudgetStatus): BudgetTransition[] {
  return getBudgetTransitions(from).filter((transition) => !transition.system);
}

/** Look up a specific transition by its action; `undefined` when not legal from `from`. */
export function findBudgetTransition(
  from: BudgetStatus,
  action: BudgetTransitionAction,
): BudgetTransition | undefined {
  return getBudgetTransitions(from).find((transition) => transition.action === action);
}

/** Whether a direct `from → to` transition exists (any action). */
export function canTransitionBudget(from: BudgetStatus, to: BudgetStatus): boolean {
  return getBudgetTransitions(from).some((transition) => transition.to === to);
}

/** Budget metadata and lines may only be edited directly while in DRAFT. */
export function isBudgetEditable(status: BudgetStatus): boolean {
  return status === BudgetStatus.Draft;
}

/** Statuses that surface a budget in the approver work queue (a review is pending/in progress). */
export const BUDGET_REVIEW_PENDING_STATUSES: readonly BudgetStatus[] = [
  BudgetStatus.ReadyForReview,
  BudgetStatus.InReview,
];

/** Whether a status represents a pending/in-progress review (drives work-queue visibility). */
export function isBudgetReviewPending(status: BudgetStatus): boolean {
  return BUDGET_REVIEW_PENDING_STATUSES.includes(status);
}
